import React, { useState } from 'react';
import SEO from '../SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Activity, BarChart2, CheckCircle, Check, X, ChevronDown, ChevronUp, Globe, MousePointer, Shield, TrendingUp, Zap, Target, Layout, Clock, Play, Server, Lock, Code , Star} from 'lucide-react';

interface LandingPageProps {
    onLogin?: () => void;
    onNavigate?: (page: string) => void;
}

const FrOrganicWebsiteTraffic: React.FC<LandingPageProps> = ({ onLogin, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);

    // Schema.org Structured Data
    const schema = [
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Générateur de Trafic Web Organique",
            "description": "Service de trafic organique pour analyse SEO, mesure du CTR et engagement visible dans GA4 avec routage résidentiel.",
            "brand": { "@type": "Brand", "name": "Traffic Creator" }
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://traffic-creator.com" },
                { "@type": "ListItem", "position": 2, "name": "Trafic Web Organique", "item": "https://traffic-creator.com/fr/acheter-trafic-organique" }
            ]
        }
    ];

    const benefits = [
        {
            icon: <Search className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Analyser le CTR organique",
            desc: "Orientez des visites via des parcours proches de la recherche pour analyser le CTR, la profondeur de session et l’engagement par requête dans Analytics."
        },
        {
            icon: <Clock className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Augmenter le Temps de Séjour",
            desc: "Les visiteurs restent sur votre site pendant des minutes, parcourent le contenu et interagissent, réduisant considérablement les taux de rebond."
        },
        {
            icon: <Globe className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Couverture de trafic local",
            desc: "Ciblez le trafic de villes ou régions spécifiques pour comparer la qualité d’engagement local entre marchés."
        },
        {
            icon: <Shield className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Comportement de navigation configurable",
            desc: "Les mouvements de souris, le défilement et les clics sont randomisés pour modéliser des parcours réalistes dans les tests Analytics."
        }
    ];

    const comparisons = [
        { feature: "Source de Trafic", us: "IPs Résidentielles Réelles", them: "Proxies de Centres de Données" },
        { feature: "Comportement", us: "Défilement, Clics, Lecture", them: "Visites Statiques" },
        { feature: "Google Analytics", us: "100% reconnu", them: "Souvent filtré / Bot" },
        { feature: "Analyse SEO", us: "Données d’engagement visibles dans GA4", them: "Neutre ou négatif" },
        { feature: "AdSense Safe", us: "Oui, vérifié", them: "Risque élevé de bannissement" },
    ];

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden">
            <SEO
                title="Acheter du Trafic Web Organique - IPs Résidentielles"
                description="Obtenez du trafic organique depuis des IP résidentielles en 2026 pour analyser le CTR, l’engagement et la qualité de session."
                keywords="trafic web organique 2026, acheter du trafic organique, trafic seo, analyse du ctr, analyse de visibilité, visiteurs réels, bot de trafic ia"
                schema={schema}
                type="product"
            />

            {/* Hreflang Tags */}

            {/* Navigation */}
            <nav className="fixed w-full top-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-gray-100/50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <Link to="/" className="flex items-center gap-2">
                            <span className="text-2xl font-black text-[#ff5a0a] tracking-tight group-hover:scale-105 transition-transform">TRAFFIC</span>
                            <span className="hidden sm:inline-block text-xs font-bold bg-black text-white px-2 py-1 rounded-sm uppercase tracking-wide">Creator</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-8">
                        <div className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wide text-gray-500">
                            <a href="#how-it-works" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Comment ça Marche
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <a href="#benefits" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Caractéristiques
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Tarifs
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </Link>
                        </div>
                        <Link to="/login" className="bg-black text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#ff5a0a] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center rounded-sm">
                            Connexion Dashboard
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-32 px-6 relative overflow-hidden bg-white">
                <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none rounded-bl-[100px]"></div>

                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
                        <div className="inline-flex items-center gap-2 bg-orange-50 text-[#ff5a0a] border border-orange-100 px-4 py-2 text-xs font-bold uppercase tracking-widest mb-8 rounded-full shadow-sm">
                            <Zap size={14} className="fill-[#ff5a0a]" /> Moteur d'Intelligence SEO
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] mb-8 text-gray-900">
                            ANALYSEZ LA QUALITÉ DU TRAFIC <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] to-orange-500">ORGANIQUE</span>.
                        </h1>
                        <p className="text-xl text-gray-500 mb-10 max-w-lg leading-relaxed font-medium">
                            Des outils de trafic web organique pour les professionnels SEO qui doivent analyser le CTR, le temps de séjour et l’engagement visible dans GA4.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/signup" className="group bg-[#ff5a0a] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-black transition-all shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                                Lancer une Campagne SEO
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <a href="#demo" className="bg-white border border-gray-200 text-gray-900 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:border-[#ff5a0a] hover:text-[#ff5a0a] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 rounded-sm group">
                                <Play size={16} className="group-hover:fill-[#ff5a0a]" /> Voir la Démo en Direct
                            </a>
                        </div>
                    </div>

                    {/* Visual: Abstract SEO Graph */}
                    <div className="relative h-[550px] perspective-1000 group">
                        <div className="absolute inset-0 bg-white border border-gray-200 shadow-2xl rounded-2xl p-6 overflow-hidden transform transition-transform duration-700 group-hover:-translate-y-2">
                            <div className="border-b border-gray-100 pb-4 mb-6 flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                            </div>

                            <div className="space-y-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className={`p-5 rounded-lg border transition-all duration-500 ${i === 1 ? 'border-[#ff5a0a]/20 bg-orange-50/30' : 'border-gray-50 bg-white'}`}>
                                        {i === 1 && <div className="absolute top-0 left-0 w-1 h-full bg-[#ff5a0a]"></div>}
                                        <div className="w-3/4 h-5 rounded-sm mb-3 bg-gray-100"></div>
                                        <div className="space-y-2">
                                            <div className="w-full h-2 bg-gray-100 rounded-sm"></div>
                                            <div className="w-5/6 h-2 bg-gray-100 rounded-sm"></div>
                                        </div>
                                        {i === 1 && (
                                            <div className="mt-4 flex items-center gap-2 text-[#ff5a0a] text-xs font-bold uppercase tracking-wider animate-pulse">
                                                <TrendingUp size={12} /> Traffic quality • CTR analysis active
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="absolute bottom-6 right-6 bg-gray-900/95 backdrop-blur-md text-white p-5 rounded-xl shadow-2xl z-20 border border-gray-800">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff5a0a] mb-2">Performance</div>
                                <div className="text-4xl font-black mb-1">18.4%</div>
                                <div className="text-xs text-gray-400 font-medium">CTR Organique (7 Jours)</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section id="benefits" className="py-32 px-6 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-24">
                        <h2 className="text-4xl font-black uppercase tracking-tight mb-6">Pourquoi la visibilité organique ?</h2>
                        <div className="w-16 h-1.5 bg-[#ff5a0a] mx-auto mb-8 rounded-full"></div>
                        <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
                            Les algorithmes de recherche évoluent. Les backlinks ne suffisent plus. Vous avez besoin de <span className="text-gray-900 font-bold">Signaux d'Utilisateur</span>. Traffic Creator génère le temps de séjour parfait.
                        </p>
                        <div className="mt-8 max-w-3xl mx-auto space-y-4 text-left md:text-center text-gray-500 leading-relaxed font-medium">
                            <p>
                                Cette page présente une approche de trafic organique pensée pour les audits et les tests de qualité de session. Au lieu de promettre un résultat de classement automatique, elle aide les équipes SEO à comparer les sources, les pays, les appareils et les parcours qui génèrent les métriques les plus utiles dans Google Analytics 4.
                            </p>
                            <p>
                                Les campagnes peuvent être utilisées pour vérifier la lisibilité d'une landing page, mesurer la profondeur de navigation, observer la réponse des formulaires et repérer les sections qui retiennent vraiment l'attention. Ces données complètent les rapports Search Console, les tests UX et les analyses de contenu, surtout lorsque le trafic naturel d'une page est encore trop faible pour tirer des conclusions fiables.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {benefits.map((b, i) => (
                            <div key={i} className="flex gap-8 p-10 border border-gray-100 hover:border-[#ff5a0a]/30 bg-white hover:bg-orange-50/20 shadow-sm transition-all duration-300 group rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-0 bg-[#ff5a0a] group-hover:h-full transition-all duration-300"></div>
                                <div className="shrink-0">
                                    <div className="bg-orange-50 w-16 h-16 flex items-center justify-center rounded-2xl group-hover:bg-[#ff5a0a] transition-all text-[#ff5a0a] group-hover:text-white">
                                        {b.icon}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#ff5a0a] transition-colors">{b.title}</h3>
                                    <p className="text-gray-500 leading-relaxed font-medium">{b.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Premium Comparison */}
            <section className="py-32 px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-4">La Qualité Compte</h2>
                        <p className="text-gray-500">Ne risquez pas votre autorité de domaine avec du trafic de bots bon marché.</p>
                    </div>

                    <div className="bg-white shadow-[0_10px_60px_rgba(0,0,0,0.05)] rounded-2xl overflow-hidden border border-gray-100">
                        <div className="grid grid-cols-3 bg-gray-900 text-white p-6 text-xs font-bold uppercase tracking-widest">
                            <div className="pl-4">Caractéristique</div>
                            <div className="text-[#ff5a0a] flex items-center gap-2"><Zap size={14} className="fill-[#ff5a0a]" /> Traffic Creator</div>
                            <div className="text-gray-500">Bots Bon Marché</div>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {comparisons.map((c, i) => (
                                <div key={i} className="grid grid-cols-3 p-6 items-center text-sm font-medium hover:bg-gray-50 transition-colors">
                                    <div className="font-bold text-gray-900 pl-4">{c.feature}</div>
                                    <div className="flex items-center gap-3 text-[#ff5a0a] font-bold">
                                        <CheckCircle size={18} className="ml-4" /> {c.us}
                                    </div>
                                    <div className="text-gray-400 flex items-center gap-3 pl-4">
                                        <X size={16} /> {c.them}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 px-6 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 px-4">
                        <span className="text-[#ff5a0a] font-bold tracking-wider uppercase text-xs mb-4 block">Testimonials</span>
                        <h2 className="text-4xl font-black text-gray-900 mb-6">Histoires de Réussite</h2>
                        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                            Découvrez comment les professionnels analysent leurs SERPs avec notre trafic organique.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=antoine_l`} alt="Antoine Laurent" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Antoine Laurent</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Expert SEO</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"La manipulation du CTR avec cette plateforme est bluffante. Mes sites grimpent systématiquement dans les résultats de recherche après quelques semaines."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=claire_d`} alt="Claire Dupont" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Claire Dupont</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Responsable E-commerce</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Nos métriques comportementales dans GA4 sont enfin parfaites. Les robots imitent la navigation humaine avec une précision redoutable."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=lucas_m`} alt="Lucas Martin" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Lucas Martin</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Fondateur d'Agence</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Oubliez les proxies de centre de données. Le réseau d'adresses IP résidentielles de Traffic Creator garantit une sécurité maximale pour nos sites."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 px-6 bg-[#ff5a0a] relative overflow-hidden">
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-8 text-white">Commencez à Ranker Maintenant</h2>
                    <p className="text-xl text-white/90 mb-12 font-medium max-w-2xl mx-auto">
                        Vos concurrents le font déjà. Égalisez les chances avec le générateur de trafic organique le plus avancé au monde.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Link to="/signup" className="bg-white text-black px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                            Essai Gratuit <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-black text-white py-20 px-6 border-t border-gray-900 text-center">
                <div className="max-w-7xl mx-auto">
                    <div className="text-3xl font-black mb-8">TRAFFIC CREATOR</div>
                    <p className="text-gray-500 max-w-md mx-auto mb-12">
                        La référence de l'industrie pour la génération de trafic SEO. Nous aidons les agences et les propriétaires de sites à améliorer signaux de recherche en toute sécurité.
                    </p>
                    <div className="text-xs text-gray-600">
                        © {new Date().getFullYear()} Traffic Creator. Tous droits réservés.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default FrOrganicWebsiteTraffic;
